import type { AxiosInstance } from 'axios';

import axios from 'axios';

import { CONFIG } from 'src/config-global';

import { toRequestFailure } from './requestError';

/** What a client reports of every answer it gets: the address asked, less the host, and the status (0 — no answer). */
export type AnswerListener = (url: string, status: number) => void;

/**
 * An api client. The browser has one — the default export, at the api's public address. The server
 * makes one for every page it renders (`src/server/api.server.ts`), at the address the api has from
 * there and with the visitor's address in its headers.
 */
export const createHttp = (
  baseURL: string,
  options: {
    headers?: Record<string, string>;
    onAnswer?: AnswerListener;
    withCredentials?: boolean;
  } = {}
): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    headers: options.headers,
    withCredentials: options.withCredentials ?? false,
  });
  const report = (config: Parameters<AxiosInstance['getUri']>[0] | undefined, status: number) => {
    if (!options.onAnswer || !config) return;

    options.onAnswer(instance.getUri(config).slice(baseURL.length), status);
  };

  instance.interceptors.request.use(
    (config) => {
      config.headers['x-timestamp'] = Date.now();
      config.headers['x-timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return config;
    },
    (error) => Promise.reject(error)
  );

  // A rejection is the api's error body plus what the interceptor used to throw away: the status
  // and the transport code. Without them a delisted car (404), a broken api (500) and no network at
  // all reached the screens as one and the same value — see requestError.ts.
  instance.interceptors.response.use(
    (response) => {
      report(response.config, response.status);
      return response;
    },
    (error) => {
      report(error?.config, error?.response?.status ?? 0);
      return Promise.reject(toRequestFailure(error));
    }
  );

  return instance;
};

/**
 * The browser's client sends the browser's credentials (api#454): the session is a cookie the api
 * set after the emailed code — `HttpOnly`, so no code here ever sees it — and `withCredentials` is
 * what makes the browser attach it to a request to another host (the api's) and keep the one a
 * sign-in sets. The api answers it only to the storefront's and the dealer cabinet's pages, by their
 * Origin, and sends `Access-Control-Allow-Credentials` for them alone.
 *
 * Before this the account token travelled as `Authorization: Bearer …` from `localStorage`, where
 * any script on the page could read it (api#402, step 2). Nothing of the kind is sent any more: a
 * request that carries an `Authorization` header is a request that asked for it by name.
 *
 * The server's client (`src/server/api.server.ts`) sends no credentials: it renders every page for
 * an anonymous visitor and has no session to speak of (README, "the server knows no session").
 */
const axiosInstance = createHttp(CONFIG.serverUrl, { withCredentials: !import.meta.env.SSR });

export default axiosInstance;
